/**
 * assets/infinite-scroll.js
 *
 * コレクションページ 無限スクロール（自動読み込み式）
 * 対象セクション：sections/main-collection-product-grid.liquid
 *
 * ---- 前提とするDOM構造（2026-07-10 main-collection-product-grid.liquid / pagination.liquid 実物で確認済み） ----
 * #ProductGridContainer
 *   > .collection
 *       > ul#product-grid[data-id="{{ section.id }}"]        … 商品カード一覧
 *       > .pagination-wrapper[data-page="{{ paginate.current_page }}"]
 *           > nav.pagination > ul.pagination__list
 *               > a.pagination__item--prev[href]              … 「次へ」リンク（Dawn標準の命名。紛らわしいが実物確認済み）
 *
 * ---- 設計方針 ----
 * 1. 自動読み込み：下端付近でSection Rendering API（?section_id=）から次ページを取得し#product-gridに追記
 * 2. メモリ負荷対策：自動読み込みは AUTO_LOAD_MAX_PAGES ページ分まで。以降は「もっと見る」ボタン式に切り替え
 *    （切り替わった後は自動読み込みには戻さない仕様）
 * 3. 戻るボタン対策：実機検証の結果、このコレクションページはShopify側が`Cache-Control: no-store`で配信しており、
 *    bfcache（ブラウザの戻る/進むキャッシュ）は常に使えないことを確認済み（Chrome DevTools「Application」→
 *    「Back/forward cache」の診断で`MainResourceHasCacheControlNoStore`を確認、2026-07-13）。
 *    そのため「戻ると常に最初のページに戻る」という割り切り仕様を採用。URLの?page=を書き換える処理は行わない
 *    （書き換えても、フルリロード時にそのページ単体の商品しか復元されず、かえって分かりにくいため）
 * 4. no-JS・fetch失敗時のフォールバック：標準ページネーションはDOM上に残し、JS初期化成功時のみCSSで非表示にする
 * 5. facets.js（絞り込み・並び替え）との共存：#ProductGridContainer本体は未確認のため、
 *    「直下の子要素が丸ごと置き換わったらMutationObserverで検知し再初期化する」という防御的な設計にしています
 *
 * ---- 変更履歴 ----
 * 2026-07-13：sentinelが:emptyセレクタ等でdisplay:noneにされる不具合を修正（ゼロ幅スペースを追加）
 * 2026-07-13：bfcacheが常に無効と判明したため、history.replaceStateによるURL更新処理を削除（案B採用）
 */
(function () {
  var AUTO_LOAD_MAX_PAGES = 4; // 自動読み込みの上限ページ数（調整可）

  function initAllContainers() {
    document.querySelectorAll('#ProductGridContainer').forEach(function (outer) {
      if (outer.dataset.infiniteScrollObserving !== 'true') {
        outer.dataset.infiniteScrollObserving = 'true';
        observeOuterReplacement(outer);
      }
      initOuter(outer);
    });
  }

  function initOuter(outer) {
    if (outer.dataset.infiniteScrollInit === 'true') return;

    var grid = outer.querySelector('#product-grid');
    var paginationWrapper = outer.querySelector('.pagination-wrapper');
    if (!grid || !paginationWrapper || grid.tagName !== 'UL') return;

    var nextLink = paginationWrapper.querySelector('a.pagination__item--prev[href]');
    if (!nextLink) return; // 1ページに収まる・最終ページ等

    outer.dataset.infiniteScrollInit = 'true';

    var state = {
      grid: grid,
      paginationWrapper: paginationWrapper,
      nextUrl: nextLink.getAttribute('href'),
      autoLoadedCount: 0,
      loading: false,
      observer: null,
      sentinel: null,
      loadMoreBtn: null,
      loadingIndicator: null
    };

    paginationWrapper.classList.add('infinite-scroll-hidden-pagination');

    state.sentinel = document.createElement('div');
    state.sentinel.className = 'infinite-scroll-sentinel';
    state.sentinel.setAttribute('aria-hidden', 'true');
    state.sentinel.textContent = '\u200B'; // ゼロ幅スペース：:emptyセレクタ等でdisplay:noneにされるのを防ぐ
    paginationWrapper.parentNode.insertBefore(state.sentinel, paginationWrapper);

    state.loadingIndicator = document.createElement('p');
    state.loadingIndicator.className = 'infinite-scroll-loading-text';
    state.loadingIndicator.textContent = '読み込み中...';
    state.loadingIndicator.style.display = 'none';
    paginationWrapper.parentNode.insertBefore(state.loadingIndicator, paginationWrapper);

    state.loadMoreBtn = document.createElement('button');
    state.loadMoreBtn.type = 'button';
    state.loadMoreBtn.className = 'infinite-scroll-load-more button button--secondary';
    state.loadMoreBtn.textContent = 'VIEW MORE　→';
    state.loadMoreBtn.style.display = 'none';
    paginationWrapper.parentNode.insertBefore(state.loadMoreBtn, paginationWrapper);

    state.loadMoreBtn.addEventListener('click', function () {
      loadNextPage(outer, state, true);
    });

    state.observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        loadNextPage(outer, state, false);
      }
    }, { rootMargin: '0px 0px 800px 0px' });

    state.observer.observe(state.sentinel);
    outer._infiniteScrollState = state;
  }

  function loadNextPage(outer, state, manualClick) {
    if (state.loading || !state.nextUrl) return;

    if (!manualClick && state.observer === null) return; // 既にボタン式へ移行済みなのに自動発火した場合の保険

    if (!manualClick && state.autoLoadedCount >= AUTO_LOAD_MAX_PAGES) {
      switchToLoadMoreMode(state);
      return;
    }

    state.loading = true;
    state.loadingIndicator.style.display = '';
    if (manualClick) {
      state.loadMoreBtn.disabled = true;
      state.loadMoreBtn.style.display = 'none';
    }

    var url;
    try {
      url = new URL(state.nextUrl, window.location.origin);
    } catch (e) {
      state.loading = false;
      state.loadingIndicator.style.display = 'none';
      return;
    }
    url.searchParams.set('section_id', state.grid.dataset.id);

    fetch(url.toString())
      .then(function (res) {
        if (!res.ok) throw new Error('infinite-scroll fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector('#product-grid');
        var newPaginationWrapper = doc.querySelector('.pagination-wrapper');

        if (newGrid) {
          newGrid.querySelectorAll(':scope > li').forEach(function (li) {
            state.grid.appendChild(li);
          });
        }

        var newNextLink = newPaginationWrapper
          ? newPaginationWrapper.querySelector('a.pagination__item--prev[href]')
          : null;
        state.nextUrl = newNextLink ? newNextLink.getAttribute('href') : null;

        state.loading = false;
        state.loadingIndicator.style.display = 'none';

        if (state.observer) {
          // まだ自動読み込みモード
          if (!manualClick) state.autoLoadedCount += 1;
          if (!state.nextUrl) {
            state.observer.disconnect();
            state.observer = null;
            state.sentinel.remove();
          } else if (state.autoLoadedCount >= AUTO_LOAD_MAX_PAGES) {
            switchToLoadMoreMode(state);
          }
        } else {
          // ボタン式モード：次があればボタンを再表示
          state.loadMoreBtn.disabled = false;
          state.loadMoreBtn.style.display = state.nextUrl ? '' : 'none';
        }
      })
      .catch(function () {
        // 失敗時は標準ページネーションにフォールバックして復帰
        state.loading = false;
        state.loadingIndicator.style.display = 'none';
        state.paginationWrapper.classList.remove('infinite-scroll-hidden-pagination');
        if (state.observer) {
          state.observer.disconnect();
          state.observer = null;
        }
        if (state.sentinel) state.sentinel.remove();
        if (state.loadMoreBtn) state.loadMoreBtn.remove();
        if (state.loadingIndicator) state.loadingIndicator.remove();
      });
  }

  function switchToLoadMoreMode(state) {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
    if (state.sentinel) state.sentinel.remove();
    if (state.nextUrl) {
      state.loadMoreBtn.style.display = '';
      state.loadMoreBtn.disabled = false;
    }
  }

  function observeOuterReplacement(outer) {
    var mo = new MutationObserver(function () {
      if (outer.dataset.infiniteScrollInit === 'true') {
        outer.dataset.infiniteScrollInit = '';
        if (outer._infiniteScrollState && outer._infiniteScrollState.observer) {
          outer._infiniteScrollState.observer.disconnect();
        }
        outer._infiniteScrollState = null;
      }
      initOuter(outer);
    });
    mo.observe(outer, { childList: true, subtree: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllContainers);
  } else {
    initAllContainers();
  }
})();